import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, BadgeCheck, Music, Music2, Music4, Radio, Scissors, Lock, Download, Timer, Play, Pause, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { api, siteDownloadUrl } from "@/lib/api";
import { SiteFooter } from "@/components/SiteFooter";
import { MasterTrackCard } from "@/components/MasterTrackCard";
import { RevisionDialog } from "@/components/RevisionDialog";
import { UpsellConfirmDialog } from "@/components/UpsellConfirmDialog";

const NOTE_ICONS = [Music, Music2, Music4];

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Watermarked preview of an unbought take, with an add button that opens payment confirmation
const StackTakeRow = ({ siteKey, take, busy, onAdd }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      document.querySelectorAll("audio").forEach((a) => a !== el && a.pause());
      el.play().catch(() => {});
    }
  };
  return (
    <div
      data-testid={`stack-take-${take.take_number}`}
      className="flex flex-col gap-3 rounded-xl border border-white/10 bg-obsidian/70 px-4 py-4 sm:flex-row sm:items-center sm:py-3"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <button
          data-testid={`stack-take-${take.take_number}-play`}
          onClick={toggle}
          aria-label={playing ? `Pause ${take.name}` : `Play watermarked ${take.name}`}
          className="h-11 w-11 shrink-0 rounded-full bg-white/10 hover:bg-neon hover:text-black text-white flex items-center justify-center transition-colors cursor-pointer"
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <p className="font-display font-bold text-sm break-words">{take.name}</p>
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-slate-500 border border-white/15 rounded-full px-2 py-1 shrink-0">
              Watermarked
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-slate-500 leading-snug">{take.style}</p>
        </div>
      </div>
      <button
        data-testid={`stack-take-${take.take_number}-add`}
        onClick={() => onAdd(take)}
        disabled={busy}
        className="flex w-full shrink-0 items-center justify-center gap-1.5 rounded-xl border border-neon/50 text-neon px-4 py-3 text-xs font-bold hover:bg-neon hover:text-black transition-colors cursor-pointer disabled:opacity-60 sm:w-auto sm:py-2"
      >
        <Plus className="h-3.5 w-3.5" /> ADD — $23
      </button>
      <audio
        ref={audioRef}
        src={`${API_URL}/api/sites/${siteKey}/audio/${take.batch_index}/${take.take_index}`}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        preload="none"
      />
    </div>
  );
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const jobId = params.get("job_id");
  const siteKeyParam = params.get("site_key");
  const [status, setStatus] = useState(sessionId ? "verifying" : "none");
  const [site, setSite] = useState(null);
  const [viewJobId, setViewJobId] = useState(jobId);
  const [stage, setStage] = useState(
    params.get("downsell_declined") ? "gone" : params.get("declined") ? "downsell" : "upsell"
  );
  const [checkingOut, setCheckingOut] = useState(false);
  const [confirmOffer, setConfirmOffer] = useState(null);
  const [savedCard, setSavedCard] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const [reviseTarget, setReviseTarget] = useState(null); // { track, label }

  const fetchSite = async () => {
    try {
      let key = siteKeyParam;
      let vj = viewJobId;
      if (!key && vj) {
        const { data: jobData } = await api.get(`/jingles/${vj}`);
        key = jobData.site_key;
      }
      if (!key) return null;
      const { data } = await api.get(`/sites/${key}`);
      setSite(data);
      setViewJobId(data.latest_job_id || vj);
      return data;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => { fetchSite(); }, [jobId, siteKeyParam]);

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!alive) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!siteKeyParam && !viewJobId && data.site_key) {
            const { data: siteData } = await api.get(`/sites/${data.site_key}`);
            if (!alive) return;
            setSite(siteData);
            setViewJobId(siteData.latest_job_id || null);
          } else {
            fetchSite();
          }
          return;
        }
        attempts += 1;
        if (attempts > 15) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (alive) setStatus("error");
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  const startHostedCheckout = async (offer) => {
    setCheckingOut(true);
    try {
      const res = await fetch("/api/payments/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lookup_key: offer.takeAddon ? "extra_take_23" : offer.lookupKey,
          origin_url: window.location.origin,
          site_key: site?.site_key,
          job_id: viewJobId,
          take_addon: offer.takeAddon || undefined,
        }),
      });
      if (!res.ok) throw new Error("checkout failed");
      const data = JSON.parse(await res.text());
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout didn't open — try again.");
      setCheckingOut(false);
    }
  };

  const requestUpsell = async (offer) => {
    setConfirmOffer(offer);
    setSavedCard(site?.saved_card || null);
    if (site?.saved_card) {
      setLoadingCard(false);
      return;
    }
    setLoadingCard(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    try {
      const res = await fetch(`/api/payments/saved-card/${site?.site_key}`, { signal: controller.signal });
      if (!res.ok) throw new Error("saved card lookup failed");
      const data = JSON.parse(await res.text());
      setSavedCard(data.saved_card || null);
    } catch (e) {
      setSavedCard(null);
    } finally {
      clearTimeout(timeout);
      setLoadingCard(false);
    }
  };

  const confirmUpsell = async () => {
    if (!confirmOffer) return;
    setCheckingOut(true);
    let res;
    try {
      res = await fetch("/api/payments/one-click", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ site_key: site?.site_key, offer: confirmOffer.lookupKey }),
      });
    } catch (e) {
      // Ambiguous network failure: never fall through to another payment attempt
      // because the saved-card charge may still have landed.
      toast.error("Couldn't confirm the charge — refresh your downloads before trying again.");
      setCheckingOut(false);
      return;
    }
    let data = null;
    try {
      data = JSON.parse(await res.text());
    } catch (e) {
      toast.error("Couldn't read the payment result — refresh your downloads before trying again.");
      setCheckingOut(false);
      return;
    }
    if (data.status === "paid" || data.status === "already_unlocked") {
      toast.success(confirmOffer.successMsg || "Added — it's unlocked in your downloads!");
      setConfirmOffer(null);
      await fetchSite();
      setCheckingOut(false);
      return;
    }
    if (data.status === "requires_action" || [400, 402, 404, 409].includes(res.status)) {
      await startHostedCheckout(confirmOffer);
      return;
    }
    toast.error(data.detail || "That saved card didn't go through — use other options.");
    setCheckingOut(false);
  };

  const paid = status === "paid" || site?.paid;
  const showUpsell = paid && site && !site.radio60_unlocked;

  // Extended Radio Edition price tiers, anchored to the server-stamped paid_ts:
  // $97 for a 3-minute launch window, $147 for the rest of the first 24 hours,
  // then the full $200. The server re-derives the tier itself at charge time.
  const RADIO_WINDOW_SECONDS = 180;
  const RADIO_DAY_SECONDS = 86400;
  const paidAt = site?.paid_ts ? new Date(site.paid_ts).getTime() : null;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!showUpsell || !paidAt) return;
    const t = setInterval(() => setNow(Date.now()), 47); // milliseconds visible — feels like it's sprinting
    return () => clearInterval(t);
  }, [showUpsell, paidAt]);
  const launchMsLeft = paidAt
    ? Math.min(RADIO_WINDOW_SECONDS * 1000, Math.max(0, RADIO_WINDOW_SECONDS * 1000 - (now - paidAt)))
    : null;
  const launchPriceLive = launchMsLeft !== null && launchMsLeft > 0;
  const dayOneLive = !launchPriceLive && paidAt && now - paidAt < RADIO_DAY_SECONDS * 1000;
  const radioPrice = !paidAt ? 97 : launchPriceLive ? 97 : dayOneLive ? 147 : 200;
  const countdownLabel = launchMsLeft !== null
    ? `${String(Math.floor(launchMsLeft / 60000)).padStart(2, "0")}:${String(Math.floor((launchMsLeft % 60000) / 1000)).padStart(2, "0")}:${String(Math.floor(launchMsLeft % 1000)).padStart(3, "0")}`
    : null;

  const radioFailed = site?.radio60_status === "failed";

  // Clean masters are rendered after payment — poll until every owed file has landed
  // and keep polling while a revision is in the studio
  useEffect(() => {
    if (!paid || !site) return;
    const owed = ["master30", "cut15", "license",
      ...(site.hold_music ? ["holdmusic"] : []),
      ...(site.radio60_unlocked ? ["radio60", "radiodonut"] : []),
      ...(site.extra_takes || []).map((t) => `take_${t.batch_index}_${t.take_index}`)];
    const ready = site.files_ready || {};
    const pendingKeys = radioFailed
      ? owed.filter((v) => !["radio60", "radiodonut"].includes(v))
      : owed;
    const filesPending = !pendingKeys.every((v) => ready[v]);
    const revising = (site.revisions || []).some((r) => ["requested", "rendering"].includes(r.status));
    if (!filesPending && !revising) return;
    const t = setTimeout(fetchSite, 6000);
    return () => clearTimeout(t);
  }, [paid, site]);

  // While the launch clock is alive the upsell sits ABOVE the master files;
  // after purchase, decline, or flatline it drops back below them
  const upsellOnTop = showUpsell && stage === "upsell" && launchPriceLive;
  // The downsell only exists while the launch clock ticks — after flatline the
  // decline link is gone, so a stale ?declined=1 URL lands back on the card.
  const effectiveStage = !launchPriceLive && stage === "downsell" ? "upsell" : stage;

  // Other takes still up for grabs — not the chosen one, not already in the stack,
  // and only when a clean master is banked and ready
  const stackTakes = (site?.versions || []).filter((v) => {
    const sel = site?.selected_take;
    if (sel && v.batch_index === sel.batch_index && v.take_index === sel.take_index) return false;
    if ((site?.extra_takes || []).some((x) => x.batch_index === v.batch_index && x.take_index === v.take_index)) return false;
    return v.master_ready;
  });

  const upsellCard = (
    <motion.div
      data-testid="upsell-card"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.6 }}
      className="mt-12 text-left rounded-3xl border border-gold/40 bg-ink p-6 sm:p-10 shadow-[0_0_60px_rgba(255,183,3,0.12)]"
    >
      <h2 data-testid="upsell-headline" className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-3 leading-snug">
        {launchPriceLive ? (
          <>WAIT! — You've Got the Jingle. <span className="text-gold">Now Turn It Into an Ad.</span></>
        ) : (
          <>Turn Your New Jingle Into a <span className="text-gold">Complete Radio Commercial</span></>
        )}
      </h2>
      <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-1.5">
        If you like the jingle, you're going to LOVE the ad!
      </p>
      <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-5">
        We'll turn your jingle into a professionally produced extended radio and podcast
        commercial with your music, your message, and a professional voice-over.
      </p>
      {launchPriceLive && (
        <div data-testid="upsell-countdown" className="rounded-2xl border-2 border-coral/60 bg-coral/10 px-4 py-3.5 mb-4">
          <div className="flex items-center gap-2.5 flex-wrap">
            <Timer className="h-5 w-5 text-coral animate-pulse" />
            <span className="font-mono text-2xl sm:text-3xl font-extrabold text-coral tracking-widest tabular-nums">
              {countdownLabel}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-coral/80">until offer expires</span>
          </div>
          <p className="text-xs text-slate-300 mt-2 leading-snug">
            The <span className="text-gold font-bold">$97</span> launch price is available right now only.
            When this clock hits zero you will not see this offer again.
          </p>
        </div>
      )}
      {launchMsLeft === 0 && (
        <p data-testid="upsell-price-expired" className="text-xs text-slate-500 mb-4 flex items-center gap-1.5">
          <Timer className="h-3.5 w-3.5" /> {dayOneLive
            ? "Time of death: confirmed for the $97 launch price — today it's $147, and after 24 hours it rises to the full $200."
            : "The launch and day-one prices are gone — the Radio Edition now walks the earth at $200."}
        </p>
      )}
      <div data-testid="upsell-price-block" className="flex flex-wrap items-end gap-3 mb-5">
        {launchPriceLive ? (
          <>
            <span className="font-display text-3xl sm:text-4xl font-extrabold text-gold tracking-tight">Add It Now for $97</span>
            <span className="text-slate-500 line-through text-lg mb-1">Regular $1,500</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-400/40 rounded-full px-3 py-1.5 mb-1.5">Save $1,403</span>
          </>
        ) : dayOneLive ? (
          <>
            <span className="text-slate-500 line-through text-lg mb-1">Regular $1,500</span>
            <span className="font-display text-3xl sm:text-4xl font-extrabold text-gold tracking-tight">Today $147</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-400/40 rounded-full px-3 py-1.5 mb-1.5">Save $1,353</span>
          </>
        ) : (
          <>
            <span className="font-display text-3xl sm:text-4xl font-extrabold text-gold tracking-tight">$200</span>
            <span className="text-slate-500 line-through text-lg mb-1">Regular $1,500</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-400/40 rounded-full px-3 py-1.5 mb-1.5">Save $1,300</span>
          </>
        )}
      </div>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-neon mb-3">You'll get:</p>
      <ul data-testid="upsell-get-list" className="space-y-2.5 mb-6">
        {[
          "Finished extended radio & podcast commercial",
          "Professional voice-over",
          "Your jingle opening and closing the ad",
          "Music-only \u201Cdonut\u201D version",
          "Unlimited lifetime commercial rights",
        ].map((text) => (
          <li key={text} className="flex items-start gap-3">
            <span className="mt-0.5 h-6 w-6 shrink-0 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center">
              <Check className="h-3 w-3 text-gold" />
            </span>
            <span className="text-sm text-slate-200 leading-snug">{text}</span>
          </li>
        ))}
        <li data-testid="upsell-revisions-note" className="flex items-start gap-3">
          <span className="mt-0.5 h-6 w-6 shrink-0 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center">
            <Check className="h-3 w-3 text-gold" />
          </span>
          <span className="text-sm text-slate-200 leading-snug">3 revision rounds</span>
        </li>
      </ul>
      <p className="font-display font-bold text-base sm:text-lg text-white">Your jingle is already done.</p>
      <p className="text-slate-400 text-sm sm:text-base mb-6">Now turn it into an ad you can actually run.</p>
      <div className="flex flex-col items-center gap-3.5">
        <button
          data-testid="upsell-accept-btn"
          onClick={() => requestUpsell({
            lookupKey: "radio_60",
            title: "Extended Radio Edition",
            price: radioPrice,
            successMsg: "Extended Radio Edition unlocked — it's in your downloads!",
          })}
          disabled={checkingOut}
          className="w-full sm:w-auto bg-gradient-to-r from-gold to-[#FB8500] text-black font-extrabold px-8 py-4 rounded-xl shadow-[0_0_30px_rgba(255,183,3,0.35)] hover:scale-[1.02] active:scale-[0.98] transition-transform cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2.5"
        >
          {checkingOut ? <Loader2 className="h-5 w-5 animate-spin" /> : <Radio className="h-5 w-5" />}
          YES, ADD MY EXTENDED RADIO COMMERCIAL FOR ${radioPrice}
        </button>
        {launchPriceLive && (
          <button
            data-testid="upsell-decline-btn"
            onClick={() => setStage("downsell")}
            className="text-sm text-slate-500 hover:text-slate-300 underline underline-offset-4 cursor-pointer"
          >
            No thanks — I'll pay more later
          </button>
        )}
      </div>
      <p className="mt-4 text-xs text-slate-600">
        One click. No additional checkout required — confirm with the card you just used, or pick another payment option on the next step.
      </p>
    </motion.div>
  );

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-obsidian text-white relative overflow-hidden">
      {paid && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          {[...Array(14)].map((_, i) => {
            const Icon = NOTE_ICONS[i % 3];
            return (
              <Icon
                key={i}
                className="absolute float-note"
                style={{
                  left: `${4 + i * 7}%`, bottom: "-6%",
                  animationDelay: `${i * 0.35}s`,
                  color: ["#FFB703", "#FF0055", "#00F0FF"][i % 3],
                }}
              />
            );
          })}
        </div>
      )}
      <main className="relative z-10 mx-auto max-w-3xl px-4 sm:px-8 pt-24 pb-24 text-center">
        {status === "verifying" && (
          <div data-testid="payment-verifying">
            <Loader2 className="h-12 w-12 text-gold animate-spin mx-auto mb-6" />
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Confirming your payment...</h1>
            <p className="text-slate-400 mt-4">Stripe is stamping your license. One second.</p>
          </div>
        )}
        {paid && (
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
            <BadgeCheck className="h-16 w-16 text-emerald-400 mx-auto mb-6" />
            <h1 data-testid="payment-success-heading" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              It's official — <span className="text-gold">this jingle is yours.</span>
            </h1>
            <p className="text-slate-400 text-lg mt-5 max-w-xl mx-auto">
              Clean, full-quality, licensed for your business forever. Play every
              cut below, download them all, and put them to work.
            </p>
            {upsellOnTop && upsellCard}
            {site && (
              <div className="mt-10 text-left">
                <div data-testid="unlocked-downloads" className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-6">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-400">
                      Your master files — unwatermarked, full quality
                    </p>
                    {site.revision_rounds && (
                      <p data-testid="revision-rounds-remaining" className="font-mono text-[11px] uppercase tracking-[0.15em] text-gold">
                        {site.revision_rounds.remaining} revision round{site.revision_rounds.remaining === 1 ? "" : "s"} left
                      </p>
                    )}
                  </div>
                  <div className="space-y-3">
                    {[
                      { track: "master30", label: "Main Jingle" },
                      { track: "cut15", label: "Short Hook" },
                      ...(site.hold_music ? [{ track: "holdmusic", label: "Instrumental Hold Music" }] : []),
                      ...(site.radio60_unlocked ? [{ track: "radio60", label: "Extended Radio Edition" }] : []),
                      ...((site.radio60_unlocked && ((site.files_ready || {}).radiodonut || ["recovering", "failed"].includes(site.radio60_status)))
                        ? [{ track: "radiodonut", label: "Music-Only Donut", canRevise: false, revisionTrack: "radio60" }]
                        : []),
                    ].map((t) => (
                      <MasterTrackCard
                        key={t.track}
                        siteKey={site.site_key}
                        sessionId={sessionId}
                        track={t.track}
                        label={t.label}
                        latestVersion={(site.track_versions || {})[t.track] || 1}
                        ready={(site.files_ready || {})[t.track]}
                        revisionInFlight={(site.revisions || []).some(
                          (r) => r.track === (t.revisionTrack || t.track) && ["requested", "rendering"].includes(r.status)
                        )}
                        failed={site.radio60_status === "failed" && ["radio60", "radiodonut"].includes(t.track)}
                        recovering={site.radio60_status === "recovering" && ["radio60", "radiodonut"].includes(t.track)}
                        canRevise={t.canRevise !== false}
                        onRevise={(track, label, version) => setReviseTarget({ track, label, version })}
                      />
                    ))}
                    {(site.extra_takes || []).map((t) => (
                      <MasterTrackCard
                        key={`take_${t.batch_index}_${t.take_index}`}
                        siteKey={site.site_key}
                        sessionId={sessionId}
                        track={`take_${t.batch_index}_${t.take_index}`}
                        label={`Take ${t.take_number} (Bonus)`}
                        latestVersion={1}
                        ready={t.ready}
                        revisionInFlight={false}
                        canRevise={false}
                        onRevise={() => {}}
                      />
                    ))}
                    <a
                      data-testid="download-license"
                      href={siteDownloadUrl(site.site_key, "license", sessionId)}
                      download
                      className="flex items-center justify-between gap-2 rounded-xl border border-emerald-400/30 bg-obsidian/70 px-4 py-3.5 text-sm text-slate-200 hover:border-emerald-400 hover:bg-emerald-400/10 transition-colors"
                    >
                      <span>Commercial License <span className="text-slate-500">· PDF</span></span>
                      <Download className="h-4 w-4 text-emerald-400" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {showUpsell && effectiveStage === "upsell" && !upsellOnTop && upsellCard}

            {showUpsell && effectiveStage === "downsell" && (
              <motion.div
                data-testid="downsell-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mt-12 text-left rounded-3xl border border-coral/50 bg-ink p-6 sm:p-10 shadow-[0_0_60px_rgba(255,0,85,0.12)]"
              >
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-coral mb-3">Today only</p>
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                  Save it from the <span className="text-coral">cutting room floor.</span>
                </h2>
                <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-6">
                  Fair enough — it's not for everyone. But this take gets deleted when your
                  session ends. Take the Extended Radio Edition home today for just{" "}
                  <span className="text-white font-bold">$67</span>{" "}
                  <span className="line-through text-slate-600">$97</span> — same instant
                  unlock, same lifetime license — just without the 3 revision rounds.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <button
                    data-testid="downsell-accept-btn"
                    onClick={() => requestUpsell({
                      lookupKey: "radio_60_downsell",
                      title: "Extended Radio Edition",
                      price: 67,
                      successMsg: "Extended Radio Edition rescued — it's in your downloads!",
                    })}
                    disabled={checkingOut}
                    className="w-full sm:w-auto bg-gradient-to-r from-coral to-[#FF5000] text-white font-extrabold px-8 py-4 rounded-xl shadow-[0_0_30px_rgba(255,0,85,0.35)] hover:scale-[1.02] active:scale-[0.98] transition-transform cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2.5"
                  >
                    {checkingOut ? <Loader2 className="h-5 w-5 animate-spin" /> : <Scissors className="h-5 w-5" />}
                    RESCUE IT FOR $67
                  </button>
                  <button
                    data-testid="downsell-decline-btn"
                    onClick={() => setStage("gone")}
                    className="text-sm text-slate-500 hover:text-slate-300 underline underline-offset-4 cursor-pointer"
                  >
                    I'll pass — delete it
                  </button>
                </div>
                <p className="mt-4 text-xs text-slate-600">
                  Confirm with the card you just used, or choose another payment option on the next step.
                </p>
              </motion.div>
            )}

            {showUpsell && effectiveStage === "gone" && (
              <p data-testid="upsell-declined-note" className="mt-12 text-sm text-slate-500 flex items-center justify-center gap-2">
                <Lock className="h-4 w-4" /> No worries — your Main Jingle and Short Hook are yours forever.
              </p>
            )}

            {site?.radio60_unlocked && site?.radio60_status !== "failed" && (
              <p data-testid="radio60-unlocked-note" className="mt-10 text-sm text-emerald-400 font-semibold">
                Extended Radio Edition: unlocked and added to your tracks above.
              </p>
            )}
            {site?.radio60_status === "recovering" && (
              <div data-testid="radio60-recovering-note" className="mt-10 rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
                The band is still polishing the radio mix and it’s taking a little longer than expected. No action is needed — this page will update.
              </div>
            )}
            {site?.radio60_status === "failed" && (
              <div data-testid="radio60-failed-note" className="mt-10 rounded-2xl border border-coral/40 bg-coral/10 px-4 py-3 text-sm text-coral">
                The band hit a tough take on the radio mix. Pepper has been alerted and will follow up — your other downloads are ready.
              </div>
            )}

            {/* Post-purchase stack: musical bed + the other versions, one click each */}
            {site && (stackTakes.length > 0 || !site.hold_music) && (
              <motion.div
                data-testid="stack-upsells"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="mt-12 text-left rounded-3xl border border-white/10 bg-ink/60 p-5 sm:p-8"
              >
                <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.25em] text-neon mb-2">Keep stacking your sound</p>
                <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight mb-1">
                  More of your jingle, on tap.
                </h2>
                <p className="text-xs text-slate-500 mb-6 leading-relaxed">
                  Confirm with the card you paid with, or use another payment option. Watermarked
                  now; the clean master lands in your downloads the moment you add it.
                </p>
                {!site.hold_music && (
                  <div data-testid="stack-holdmusic" className="flex flex-col gap-3 rounded-xl border border-gold/30 bg-obsidian/70 px-4 py-4 mb-3 sm:flex-row sm:items-center sm:py-3.5">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="h-11 w-11 shrink-0 rounded-full bg-gold/15 flex items-center justify-center">
                        <Music2 className="h-4 w-4 text-gold" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-display font-bold text-sm">Instrumental Musical Bed</p>
                        <p className="mt-0.5 text-[11px] text-slate-500 leading-snug">
                          100% unique in your jingle's style — for commercials, videos, even on-hold.
                        </p>
                      </div>
                    </div>
                    <button
                      data-testid="stack-holdmusic-btn"
                      onClick={() => requestUpsell({
                        lookupKey: "hold_music",
                        title: "Instrumental Musical Bed",
                        price: 47,
                        successMsg: "Instrumental Musical Bed added — it's in the studio!",
                      })}
                      disabled={checkingOut}
                      className="flex w-full shrink-0 items-center justify-center gap-1.5 rounded-xl border border-gold/60 text-gold px-4 py-3 text-xs font-bold hover:bg-gold hover:text-black transition-colors cursor-pointer disabled:opacity-60 sm:w-auto sm:py-2"
                    >
                      <Plus className="h-3.5 w-3.5" /> ADD — $47
                    </button>
                  </div>
                )}
                {stackTakes.length > 0 && (
                  <>
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-slate-500 mt-6 mb-3 leading-relaxed">
                      Want the other versions? $23 each — clean, no watermark
                    </p>
                    <div className="space-y-3">
                      {stackTakes.map((t) => (
                        <StackTakeRow
                          key={t.take_number}
                          siteKey={site.site_key}
                          take={t}
                          busy={checkingOut}
                          onAdd={(take) => requestUpsell({
                            lookupKey: `take_${take.batch_index}_${take.take_index}`,
                            title: take.name,
                            price: 23,
                            successMsg: `${take.name} added — the clean master is in your downloads!`,
                            takeAddon: { batch_index: take.batch_index, take_index: take.take_index },
                          })}
                        />
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
        {(status === "timeout" || status === "error") && (
          <div data-testid="payment-pending-note">
            <h1 className="font-display text-3xl font-bold tracking-tight">Still confirming...</h1>
            <p className="text-slate-400 mt-4 max-w-md mx-auto">
              Payments sometimes take a moment to land. If you were charged, your download
              links unlock automatically — head back to your jingle page in a minute.
            </p>
            {viewJobId && (
              <Link to={`/jingle-preview/${viewJobId}`} className="inline-block mt-6 text-gold underline">
                Back to your jingle
              </Link>
            )}
          </div>
        )}
      </main>
      <RevisionDialog
        open={!!reviseTarget}
        onOpenChange={(open) => !open && setReviseTarget(null)}
        siteKey={site?.site_key}
        track={reviseTarget?.track}
        trackLabel={reviseTarget?.label}
        roundsRemaining={site?.revision_rounds?.remaining ?? 0}
        currentLyrics={(site?.track_lyrics?.[reviseTarget?.track]) || (site?.lyrics || []).join("\n")}
        lyricVersions={(site?.track_lyric_versions?.[reviseTarget?.track]) || {}}
        selectedVersion={reviseTarget?.version || 1}
        onDone={fetchSite}
      />
      <UpsellConfirmDialog
        offer={confirmOffer}
        savedCard={savedCard}
        loadingCard={loadingCard}
        busy={checkingOut}
        onOpenChange={(open) => !open && setConfirmOffer(null)}
        onConfirm={confirmUpsell}
        onOtherOptions={() => confirmOffer && startHostedCheckout(confirmOffer)}
      />
      <SiteFooter />
    </div>
  );
}
